import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import NotificationBell from './NotificationBell';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Search, Menu, Plus, User, LogOut, Activity, BookOpen, Shield, Moon, Sun, X, Home, PenLine } from 'lucide-react';
import { login as apiLogin, register as apiRegister } from '../lib/api';
import { toast } from 'sonner';

export default function Navbar() {
  const { user, loginUser, logoutUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authForm, setAuthForm] = useState({ email: '', username: '', password: '' });
  const [authLoading, setAuthLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/recherche?q=${encodeURIComponent(searchQuery.trim())}`);
      setMobileMenuOpen(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      if (authMode === 'login') {
        const res = await apiLogin({ email: authForm.email, password: authForm.password });
        loginUser(res.data.token, res.data.user);
        toast.success('Connecte avec succes !');
      } else {
        const res = await apiRegister(authForm);
        loginUser(res.data.token, res.data.user);
        toast.success('Compte cree avec succes !');
      }
      setShowAuth(false);
      setAuthForm({ email: '', username: '', password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setAuthLoading(false);
    }
  };

  const navLinks = [
    { href: '/', label: 'Accueil', icon: Home },
    { href: '/annuaire', label: 'Communautes', icon: BookOpen },
    ...(user && (user.role === 'admin' || user.role === 'mod') ? [{ href: '/admin', label: 'Admin', icon: Shield }] : []),
  ];

  const navigateTo = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header className="sticky top-0 z-50 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80" data-testid="navbar">
        <div className="mx-auto max-w-7xl flex items-center justify-between px-3 sm:px-4 lg:px-6 h-14">
          {/* Left: Logo + Hamburger */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Mobile hamburger */}
            <button
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-[hsl(var(--muted))] transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            {/* Logo */}
            <Link to="/" className="flex items-center gap-1.5 shrink-0" data-testid="logo-link" onClick={() => setMobileMenuOpen(false)}>
              <img src="/medsynapse-logo.jpeg" alt="MedSynapse" className="h-8 w-auto rounded-lg" />
              <span className="font-heading font-bold text-base sm:text-lg hidden xs:block">MedSynapse</span>
            </Link>

            {/* Desktop nav links */}
            <nav className="hidden md:flex items-center gap-1 ml-2">
              {navLinks.map(link => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                    location.pathname === link.href
                      ? 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]'
                      : 'text-muted-foreground hover:bg-[hsl(var(--muted))] hover:text-foreground'
                  }`}
                >
                  <link.icon className="w-4 h-4" />
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Center: Search (desktop only) */}
          <form onSubmit={handleSearch} className="hidden md:block flex-1 max-w-md mx-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="global-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher posts, communautes, agents..."
                className="pl-9 h-9 bg-[hsl(var(--muted))] border-transparent focus:border-[hsl(var(--ring))]"
              />
            </div>
          </form>

          {/* Right: Actions */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="w-8 h-8 p-0"
              data-testid="theme-toggle"
              aria-label={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>

            {user ? (
              <>
                <NotificationBell />
                <Button
                  size="sm"
                  onClick={() => navigateTo('/nouveau-post')}
                  className="hidden sm:flex gap-1.5"
                  data-testid="create-post-button"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden lg:inline">Creer</span>
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-1 px-1.5" data-testid="user-menu-button">
                      <div className="w-7 h-7 rounded-full bg-[hsl(var(--primary))] flex items-center justify-center">
                        <span className="text-xs font-bold text-white">{user.username?.[0]?.toUpperCase()}</span>
                      </div>
                      <span className="hidden lg:block text-sm max-w-[80px] truncate">{user.username}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigateTo(`/profil/${user._id}`)}>
                      <User className="w-4 h-4 mr-2" /> Mon profil
                    </DropdownMenuItem>
                    {/* Mobile: show create post in dropdown */}
                    <DropdownMenuItem className="sm:hidden" onClick={() => navigateTo('/nouveau-post')}>
                      <PenLine className="w-4 h-4 mr-2" /> Creer un post
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => { logoutUser(); setMobileMenuOpen(false); }}>
                      <LogOut className="w-4 h-4 mr-2" /> Deconnexion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button size="sm" onClick={() => { setShowAuth(true); setAuthMode('login'); }} data-testid="login-button" className="text-xs sm:text-sm">
                Connexion
              </Button>
            )}
          </div>
        </div>

        {/* Mobile Menu Drawer */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t bg-card">
            <div className="px-3 py-3 space-y-2">
              {/* Mobile Search */}
              <form onSubmit={handleSearch}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher..."
                    className="pl-9 h-10 bg-[hsl(var(--muted))] border-transparent"
                  />
                </div>
              </form>

              {/* Mobile Nav Links */}
              <nav className="space-y-1">
                {navLinks.map(link => (
                  <button
                    key={link.href}
                    onClick={() => navigateTo(link.href)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname === link.href
                        ? 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]'
                        : 'text-muted-foreground hover:bg-[hsl(var(--muted))] hover:text-foreground'
                    }`}
                  >
                    <link.icon className="w-5 h-5" />
                    {link.label}
                  </button>
                ))}
                {user && (
                  <button
                    onClick={() => navigateTo('/nouveau-post')}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-[hsl(var(--muted))] hover:text-foreground transition-colors"
                  >
                    <PenLine className="w-5 h-5" />
                    Creer un post
                  </button>
                )}
              </nav>
            </div>
          </div>
        )}
      </header>

      {/* Mobile menu backdrop */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
          style={{ top: 'auto' }}
        />
      )}

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md z-[100]">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {authMode === 'login' ? 'Connexion' : 'Creer un compte'}
            </DialogTitle>
            <DialogDescription>
              {authMode === 'login'
                ? 'Connectez-vous pour participer aux discussions.'
                : 'Rejoignez la communaute medicale MedSynapse.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAuth} className="space-y-4">
            {authMode === 'register' && (
              <Input
                data-testid="auth-username-input"
                placeholder="Nom d'utilisateur"
                value={authForm.username}
                onChange={(e) => setAuthForm({...authForm, username: e.target.value})}
                required
              />
            )}
            <Input
              data-testid="auth-email-input"
              type="email"
              placeholder="Email"
              value={authForm.email}
              onChange={(e) => setAuthForm({...authForm, email: e.target.value})}
              required
            />
            <Input
              data-testid="auth-password-input"
              type="password"
              placeholder="Mot de passe"
              value={authForm.password}
              onChange={(e) => setAuthForm({...authForm, password: e.target.value})}
              required
              minLength={6}
            />
            <Button type="submit" className="w-full" disabled={authLoading} data-testid="auth-submit-button">
              {authLoading ? 'Chargement...' : authMode === 'login' ? 'Se connecter' : 'Creer le compte'}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              {authMode === 'login' ? (
                <>Pas de compte ? <button type="button" onClick={() => setAuthMode('register')} className="text-[hsl(var(--primary))] font-medium hover:underline">Creer un compte</button></>
              ) : (
                <>Deja un compte ? <button type="button" onClick={() => setAuthMode('login')} className="text-[hsl(var(--primary))] font-medium hover:underline">Se connecter</button></>
              )}
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
